import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import Modal from '@mui/material/Modal';
import {
  Box,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { showErrorToaster } from '../../Common/ComponentToast/ComponentSuccessToasts';
import { RootState } from '../../redux/rootState';
import { getSyncedJobOrder } from '../JobScheduling/UnscheduledJobs/addJob/redux/jobScheduleSlice';
import { exportToExcelData } from '../../Common/PageHeader/helper';
import { Cancel } from '../../media/images/Cancel';
import { PrintIcon } from '../../assets/TableIcons';
import DataTable from '../../Common/DataTable';
import { setLoader } from '../../redux/loaderSlice';
import { months } from '../calender/utils/constant';
import { Entries } from '../../Common/ComponentSelectInput/types';
import { SyncedJobOrderReportTableData } from './utils/TypeDashboard';

interface IForecastReportModal {
  open: boolean;
  handleClose: () => void;
}

export default function SyncedJobOrderModal({
  open, handleClose,
}: IForecastReportModal) {
  const Datatablestyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    bgcolor: 'background.paper',
    borderRadius: '6px',
    boxShadow: 24,
    p: '6px 24px 24px 24px',
    height: 'auto',
    minHeight: '450px',
    '@media (max-width: 768px)': {
      p: '6px 6px 15px',
      height: 'auto',
    },
  };

  const dispatch = useDispatch();
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(today.getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(today.getFullYear()));

  const getData = () => {
    if (selectedMonth && selectedYear) {
      const payload = {
        month: selectedMonth?.length > 1 ? selectedMonth : `0${selectedMonth}`,
        year: selectedYear,
      };
      dispatch(getSyncedJobOrder(payload));
    }
  };

  useEffect(() => {
    getData();
  }, [selectedMonth, selectedYear]);

  const { syncedJobOrderData, isLoading } = useSelector((state: RootState) => state.jobScheduleReducer);

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  const receivedReports = useMemo(() => {
    const received = syncedJobOrderData?.Data?.map((item) => ({
      id: item?.JobOrderNumber,
      jobOrder: item?.JobOrderNumber,
      jobOrderDate: item?.JobDate,
      version: item?.Version,
      vessels_ToolTip: item?.Vessels,
      customerName: item?.CustomerName,
      model: item?.Model,
      serial: item?.SerialNumber,
      site: item?.SiteName,
      siteCity: item?.SiteCity,
      siteAddress: item?.SiteAddress,
      poNumber: item?.PoNumber,
    }));
    return received;
  }, [syncedJobOrderData]);

  const receivedColumns = [
    { key: 'jobOrder', label: 'Job Order #' },
    { key: 'jobOrderDate', label: 'Job Order Date' },
    { key: 'version', label: 'Version used for Sync' },
    { key: 'vessels_ToolTip', label: 'Vessels' },
    { key: 'customerName', label: 'Customer' },
    { key: 'model', label: 'Model' },
    { key: 'serial', label: 'Serial #' },
    { key: 'site', label: 'Site' },
    { key: 'siteCity', label: 'Site City' },
    { key: 'siteAddress', label: 'Site Address' },
    { key: 'poNumber', label: 'PO Number' },
  ];

  const downloadExcel = () => {
    if (syncedJobOrderData?.Data?.length > 0) {
      const exportedData = syncedJobOrderData?.Data?.map((items) => ({
        'Job Order #': items?.JobOrderNumber,
        'Job Order Date': items?.JobDate,
        'Version used for Sync': items?.Version,
        Vessels: items?.Vessels?.join(', '),
        Customer: items?.CustomerName,
        Model: items?.Model,
        'Serial #': items?.SerialNumber,
        Site: items?.SiteName,
        'Site City': items?.SiteCity,
        'Site Address': items?.SiteAddress,
        'PO Number': items?.PoNumber,
      }));
      exportToExcelData(
        {
          data: exportedData?.length > 0 ? exportedData : [],
          removeSn: true,
        },
        `SYNCED JOB ORDERS REPORT (${selectedMonth?.length > 1 ? selectedMonth : `0${selectedMonth}`}/${selectedYear})`,
      );
    } else {
      showErrorToaster('No records to export');
    }
  };

  const years: Entries[] = useMemo(() => {
    const arr = Array.from({ length: 2099 - 1999 }, (_, index) => index + 2000);

    return arr.map((ele) => ({
      Text: String(ele),
      Value: String(ele),
    }));
  }, []);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={Datatablestyle}>
        <div className="customerInfo_container flex-row">
          <div className="customerInfo">
            Synced Job Orders Report
          </div>
          <div className="cancel-icon" onClick={() => handleClose()}>
            <Cancel />
          </div>

        </div>
        <div className="customModalBtnContainer">
          <div className="forecastButtonContainer">
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <label style={{ fontSize: '14px', fontWeight: '500', marginRight: '5px' }}>
                Month
              </label>
              <Select
                size="small"
                value={String(selectedMonth)}
                style={{ width: '150px' }}
                onChange={(event: SelectChangeEvent<string | string>) => setSelectedMonth(event.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '220px', // Adjust the max height value as needed
                      maxWidth: '220px',
                    },
                  },
                }}
              >
                {months.map((item: Entries) => (
                  <MenuItem className="menu-item-enter" value={item.Value} key={item.Value}>
                    {item.Text}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <label style={{ fontSize: '14px', fontWeight: '500', marginRight: '5px' }}>
                Year
              </label>
              <Select
                size="small"
                value={String(selectedYear)}
                style={{ width: '100px' }}
                onChange={(event: SelectChangeEvent<string | string>) => (setSelectedYear(event.target.value))}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '220px', // Adjust the max height value as needed
                      maxWidth: '220px',
                    },
                  },
                }}
              >
                {years.map((item: Entries) => (
                  <MenuItem className="menu-item-enter" value={item.Value} key={item.Value}>
                    {item.Text}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px',
          }}
          >
            <div className="pointCursor" onClick={downloadExcel}>
              <PrintIcon />
            </div>
          </div>
        </div>
        <DataTable<SyncedJobOrderReportTableData>
          data={receivedReports || []}
          columns={receivedColumns}
          handleData={() => {}}
          totalRecords={10}
        />
      </Box>
    </Modal>
  );
}
